/**
 * Cross-tab auth coordination via BroadcastChannel.
 *
 * Mirrors apps/client. Birdbrain admin tabs share the same parent-domain
 * `refreshToken` cookie; this channel keeps in-memory access tokens and login state
 * in sync across tabs without round-tripping through the server.
 */

const CHANNEL_NAME = 'taka-auth'

type AuthMessage = { type: 'logout' } | { type: 'token-refreshed'; accessToken: string }

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (channel) return channel
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    return channel
  } catch {
    return null
  }
}

export function broadcastLogout(): void {
  const ch = getChannel()
  if (!ch) return
  try {
    ch.postMessage({ type: 'logout' } satisfies AuthMessage)
  } catch {
    /* swallow */
  }
}

export function broadcastTokenRefreshed(accessToken: string): void {
  const ch = getChannel()
  if (!ch) return
  try {
    ch.postMessage({ type: 'token-refreshed', accessToken } satisfies AuthMessage)
  } catch {
    /* swallow */
  }
}

export function subscribeToAuthBroadcast(handler: (message: AuthMessage) => void): () => void {
  const ch = getChannel()
  if (!ch) return () => undefined
  const listener = (event: MessageEvent<AuthMessage>) => {
    if (event.data && typeof event.data.type === 'string') {
      handler(event.data)
    }
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}
